import { useQuery } from '@apollo/client';
import { ShoppingBagOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { WidgetIcon } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TlscpEntityVariant, TsrctCommentParentVariant } from '@/generated/gql/graphql';
import { CommentsPopoverButton } from '@/legacy/components/CommentsPopoverButton/CommentsPopoverButton';
import { ModuleEntityType } from '~/common/types';
import { ContractDemandStatusChip } from '~/components/ContractDemandStatusChip/ContractDemandStatusChip';
import { ModuleContextProvider, ModuleContextProviderValue } from '~/components/ModuleContextProvider';
import { ModulePage } from '~/components/ModulePage/ModulePage';
import { useModulePageConfig } from '~/components/ModulePageConfigProvider';
import { ModulePageHeader } from '~/components/ModulePageHeader/ModulePageHeader';
import { ModuleSlot } from '~/components/ModuleSlot/ModuleSlot';
import { ModuleSlotId } from '~/modules';
import { BusinessLogPopoverButton } from '~/modules/BusinessLogWidget/components/BusinessLogPopoverButton/BusinessLogPopoverButton.tsx';
import { useDemandDetail, useTabId } from '~/modules/Module.hooks';
import { ContractDemandOverFlowMenu } from '~/routes/autonomousSourcing/ContractDemandDetail/components/ContractDemandDetailOverFlowMenu.tsx';
import { RouteId } from '~/routes/RouteId';

import { CONTRACT_DEMAND_DETAIL_QUERY } from './ContractDemandDetail.gql';

export const ContractDemandDetail = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'ContractDemandDetail' });
  const { demandId } = useDemandDetail();
  const { tabId } = useTabId();

  const { modules, tabs, maxWidth } = useModulePageConfig({ pageId: RouteId.contractDemandDetail(), tabId });

  const { data } = useQuery(CONTRACT_DEMAND_DETAIL_QUERY, {
    variables: {
      id: demandId,
    },
  });

  const { sequenceNumber, status } = data?.tesseract.contractDemand || {};
  const contextValue = useMemo<ModuleContextProviderValue>(
    () => ({ entity: ModuleEntityType.CONTRACT_DEMAND, entityId: demandId }),
    [demandId],
  );
  return (
    <>
      <ModulePage
        maxWidth={maxWidth}
        header={
          <ModulePageHeader
            title={
              data && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6" color="text.primary">
                    {t('title', { sequenceNumber })}
                  </Typography>
                  {status && <ContractDemandStatusChip status={status} />}
                </Stack>
              )
            }
            icon={<WidgetIcon icon={<ShoppingBagOutlined fontSize="small" />} />}
            right={
              <Stack
                spacing={3}
                direction="row"
                sx={{
                  alignItems: 'center',
                }}
              >
                <CommentsPopoverButton entityId={demandId} entityVariant={TsrctCommentParentVariant.CONTRACT_DEMAND} />
                <BusinessLogPopoverButton entityId={demandId} entityVariant={TlscpEntityVariant.CONTRACT_DEMAND} />
                {demandId && <ContractDemandOverFlowMenu id={demandId} status={status} />}
              </Stack>
            }
            tabs={tabs}
          />
        }
        modules={
          <ModuleContextProvider value={contextValue}>
            <ModuleSlot id={ModuleSlotId.MAIN} modules={modules} />
          </ModuleContextProvider>
        }
      />
    </>
  );
};
